import { useDebounce } from '#base/util/hook.js';
import { DEFAULT_COLORS } from '@c2s-npm/plumlib';
import { useEffect, useMemo, useRef, useState } from 'react';
import { CirclePicker, SketchPicker } from 'react-color';

import { Profile, Transform, eIntent, convert } from 'jscolorengine';
import { loadImage, convertSVGToPNG, uploadImageAsync, convertImageToPNGAsync } from '#base/util/image.js';

export * as ReactColor from 'react-color';

export const getGrayScale = (color) => {
  const [r, g, b] = getRgb(color);
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return luminance;
};

export const getRgb = (color) => {
  if (Array.isArray(color)) {
    return color;
  } else {
    const [_, hex] = color.match(/^(?:#|0x)?([0-9a-f]+)$/i);
    if (hex) {
      const split =
        hex.length === 3 || hex.length === 4
          ? hex.match(/(.{1})/g).map((v) => v + v)
          : hex.match(/(.{1,2})/g);
      const rgb = split.map((s) => parseInt(s, 16));
      return rgb;
    } else {
      const [_, rgb] = color.match(/^rgba?\(\d+,\d+,\d+(,\d+)?\)$/i);
      if (rgb) {
        return rgb.split(',').map(parseInt);
      }
    }
  }
  return null;
};

export const getAverageRgb = (colors) => {
  return colors
    .reduce(
      (a, [r, g, b]) => {
        a[0] += r;
        a[1] += g;
        a[2] += b;
        return a;
      },
      [0, 0, 0]
    )
    .map((c) => Math.round(c / colors.length));
};

// No-color is an X
const _noColorCSS = {
  background: `  linear-gradient(45deg, transparent 49%, black 49%, black 51%, transparent 51%, transparent 100%),
  linear-gradient(-45deg, transparent 49%, black 49%, black 51%, transparent 51%, transparent 100%),
  white`,
};

export const ColorPicker = ({ value, onSelect, colors = DEFAULT_COLORS, includeNocolor = false, mode }) => {
  const ref = useRef();
  const [_open, setOpen] = useState(false);
  const color = value || (includeNocolor ? value : '#000000');

  const close = useMemo(
    () => (e) => {
      if (!ref.current?.contains?.(e.target)) {
        setOpen(false);
      }
    },
    []
  );

  useEffect(() => {
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const [localColor, _setLocalColor] = useState(color);
  useEffect(() => {
    _setLocalColor(color);
  }, [color]);

  const [displayColors, _d2v, _v2d] = useMemo(() => {
    const display = colors.map((c) => c.display.toLowerCase());

    const d2v = colors.reduce((a, { print, display }) => {
      a[display.toLowerCase()] = print.toLowerCase();
      return a;
    }, {});

    const v2d = colors.reduce((a, { print, display }) => {
      a[print.toLowerCase()] = display.toLowerCase();
      return a;
    }, {});

    return [display, d2v, v2d];
  }, [colors]);

  const setColor = useDebounce((c) => {
    c !== color && !!onSelect && onSelect(c);
  }, 100);

  const setLocalColor = (color) => {
    _setLocalColor(color);
    setColor(color.hex || color);
  };

  const activeColorName = useMemo(
    () => colors.find((c) => c.display.toLowerCase() === color?.toLowerCase?.())?.name || '',
    [color, colors]
  );

  return (
    <>
      {mode !== 'pro' && (
        <div>
          {Boolean(activeColorName) && (
            <p className='t:font-normal t:text-[#757575] t:text-base'>
              Color <span className='t:text-[#1f1f1f]'>{activeColorName}</span>
            </p>
          )}
          <CirclePicker color={localColor} colors={displayColors} onChange={setLocalColor} width={'100%'} />
        </div>
      )}
      {mode === 'pro' && (
        <div className='t:flex t:justify-center t:w-full'>
          <SketchPicker
            color={localColor}
            presetColors={displayColors}
            onChange={setLocalColor}
            disableAlpha={true}
          />
        </div>
      )}
    </>
  );
  /*
  return (
    <div className='color-picker' ref={ref}>
      <div onClick={() => setOpen(!open)}>
        <div className='color-picker-selected' style={localColor ? { backgroundColor: localColor.hex || localColor } : noColorCSS}/>
      </div>
      {open && (
        <div className='color-picker-palette'>
          <div className='t:p-4 t:bg-white t:shadow t:rounded'>
            <CirclePicker color={localColor} colors={colors} onChange={setLocalColor}/>
          </div>
          {open && includeNocolor && (
            <div key='nocolor' className='color-picker-swatch' style={noColorCSS} onClick={() => onSelect(null)}></div>
          )}
        </div>
      )}
    </div>
  );
  */
};
ColorPicker.defaultProps = {
  colors: DEFAULT_COLORS,
};

const appendImage = (url) => {
  var element = document.createElement('img');
  element.src = url;
  document.body.appendChild(element);
};

export const transformColorProfile = async (src, profileUrl) => {
  let img = src;
  if (typeof src === 'string') {
    img = await loadImage(src);
  }

  appendImage(src);

  let printerProfile = new Profile();
  // await printerProfile.loadPromise('http://localhost:9000/files/6119759');
  // http://localhost:9000/files/6119759
  // http://localhost:9000/files/9057287
  printerProfile.loadURL(profileUrl, (profile) => {
    const proofTransform = new Transform({
      buildLUT: true,
      dataFormat: 'int8',
      // BPC: true,
      BPC: [true, false], // Enable blackpoint compensation on preceptional intent but not relative
    });

    // proofTransform.create('*srgb', profile, eIntent.perceptual);
    proofTransform.createMultiStage(['*srgb', eIntent.perceptual, profile, eIntent.relative, '*srgb']);

    var canvas = document.createElement('canvas');
    canvas.width = img.width;
    canvas.height = img.height;
    var ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0);
    var imageData = ctx.getImageData(0, 0, img.width, img.height);

    // convert RGB to RGB with soft proofing
    var data = proofTransform.transformArray(imageData.data, true, true, true);
    const clamped = new Uint8ClampedArray(data);
    const proofData = new ImageData(clamped, canvas.width, canvas.height);

    ctx.putImageData(proofData, 0, 0);

    // document.body.appendChild(canvas);

    const proofUrl = canvas.toDataURL('image/png');

    appendImage(proofUrl);

    // /custom/miyo/color/generate/22068/1282833/top
    return proofUrl;
  });
};
